import { useDeviceDrawerData } from '../../hooks/useDeviceDrawerData'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { usePermissions } from '@/hooks/usePermissions'

import { DrawerTabIProps } from '@/features/device-drawer'
import { DeviceType } from '@/graphql/generated/schemas'

import { AssociatedDevicesInfoModal } from '../../associated-devices-tab/components/AssociatedDevicesInfoModal'
import { AssociatedDevicesTab } from '../../associated-devices-tab/components/AssociatedDevicesTab'
import { DarkDeviceAlerts } from '../components/DarkDeviceAlerts'
import { DarkDeviceInfoTab } from '../components/DarkDeviceInfoTab'
import { DarkDeviceLogs } from '../components/DarkDeviceLogs'

/**
 * Rebrand (ENG-32123, Phase 2) tab source for `DarkDeviceDrawer`. Mirrors
 * `useDeviceDrawerTabs`'s labels + gating but supplies the dark panel twins, so
 * reskinning the tabs never touches the legacy drawers (which keep calling
 * `useDeviceDrawerTabs`).
 *
 * Reskinned: Recent Incidents (`DarkDeviceAlerts`), Device Logs
 * (`DarkDeviceLogs`), Device Info (`DarkDeviceInfoTab`). Associated Devices
 * already renders on the dark system.
 */
export const useDarkDeviceDrawerTabs = ({
  incidents,
  isIncidentsLoading,
  deviceId,
  deviceFloorId,
  deviceName,
  deviceType,
}: {
  incidents: ReturnType<typeof useDeviceDrawerData>['incidents']
  isIncidentsLoading: boolean
  deviceId: string
  deviceFloorId?: string | null
  deviceName?: string | null
  deviceType?: DeviceType
}): DrawerTabIProps[] => {
  const { isImplementer } = usePermissions()
  const isDeviceAssociationStatusEnabled = useFeatureFlag(
    FeatureFlag.deviceAssociationStatus
  )

  const associatedDevicesTab: DrawerTabIProps = {
    label: 'Associated Devices',
    icon: <AssociatedDevicesInfoModal />,
    panel: (
      <AssociatedDevicesTab
        deviceFloorId={deviceFloorId}
        deviceId={deviceId}
        deviceName={deviceName}
        deviceType={deviceType}
      />
    ),
  }

  const recentIncidentsTab: DrawerTabIProps = {
    label: 'Recent Incidents',
    icon: null,
    panel: (
      <DarkDeviceAlerts
        deviceType={deviceType}
        incidents={incidents}
        isLoading={isIncidentsLoading}
      />
    ),
  }

  const implementerTabs: DrawerTabIProps[] = isImplementer
    ? [
        {
          label: 'Device Info',
          icon: null,
          panel: <DarkDeviceInfoTab id={deviceId} />,
        },
        {
          label: 'Device Logs',
          icon: null,
          panel: <DarkDeviceLogs deviceId={deviceId} />,
        },
      ]
    : []

  return [
    ...(isDeviceAssociationStatusEnabled ? [associatedDevicesTab] : []),
    recentIncidentsTab,
    ...implementerTabs,
  ]
}
